import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { CheckCircle, XCircle, Trash2, Eye } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CardRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // all, pending, published, rejected
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      let url = `${BACKEND_URL}/api/admin/card-requests`;
      if (filter !== 'all') {
        url += `?status=${filter}`;
      }
      
      const response = await apiRequest(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setRequests(response.data.requests || []);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch requests');
      setLoading(false);
    }
  };

  const handlePublish = async (cardId, cardType) => {
    if (!window.confirm('Are you sure you want to publish this card?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/card-requests/${cardId}/publish?card_type=${cardType}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Card published successfully!');
        fetchRequests();
      } else {
        alert('Failed to publish card');
      }
    } catch (err) {
      alert('Error publishing card');
    }
  };

  const handleReject = async (cardId, cardType) => {
    if (!window.confirm('Are you sure you want to reject this card?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/card-requests/${cardId}/reject?card_type=${cardType}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Card rejected successfully!');
        fetchRequests();
      } else {
        alert('Failed to reject card');
      }
    } catch (err) {
      alert('Error rejecting card');
    }
  };

  const handleDelete = async (cardId, cardType) => {
    if (!window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/card-requests/${cardId}?card_type=${cardType}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Card deleted successfully!');
        fetchRequests();
      } else {
        alert('Failed to delete card');
      }
    } catch (err) {
      alert('Error deleting card');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-navy">Card Requests</h1>
          
          {/* Filter Tabs */}
          <div className="flex gap-2">
            {['all', 'pending', 'published', 'rejected'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === f
                    ? 'bg-navy text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
            <p className="text-gray-600">No card requests found</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-navy">{request.name}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          request.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : request.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {request.status}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          request.card_type === 'review'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {request.card_type === 'review' ? 'Review Card' : 'Digital Card'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-3">{request.short_description}</p>
                    
                    {request.artist_name && (
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold">Artist:</span> {request.artist_name} ({request.artist_email})
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-400 mt-2">
                      Created: {new Date(request.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    {request.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => handlePublish(request.id, request.card_type)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Publish
                        </Button>
                        <Button
                          onClick={() => handleReject(request.id, request.card_type)}
                          variant="outline"
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    )}
                    
                    <Button
                      onClick={() => handleDelete(request.id, request.card_type)}
                      variant="outline"
                      className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
